// frontend/src/pages/HomePage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Smartphone, 
  Wifi, 
  Zap, 
  Users, 
  Shield, 
  Clock, 
  Globe,
  CheckCircle,
  ArrowRight,
  Star,
  TrendingUp,
  Award,
  Wallet,
  X,
  Mail,
  Phone,
  MapPin,
  Info,
  Store,
  Briefcase,
  Eye
} from 'lucide-react';

const HomePage = () => {
  const [activeModal, setActiveModal] = useState(null);

  const openModal = (modal) => setActiveModal(modal);
  const closeModal = () => setActiveModal(null);

  return (
    <div className="page-container">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-neumorph border-b border-primary-100/50 shadow-neomorph">
        <div className="content-container px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2">
              <img 
                src="/fastel-logo.png" 
                alt="Fastel" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-primary-700 via-primary-600 to-primary-400 bg-clip-text text-transparent">
                Fastel
              </span>
            </div>
            
            <div className="hidden md:flex items-center space-x-1">
              <a href="#services" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors">
                Services
              </a>
              <a href="#marketplace" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors">
                Marketplace
              </a>
              <a href="#community" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors">
                Community
              </a>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-3">
              <Link to="/login" className="px-3 py-1.5 sm:px-4 sm:py-2 text-sm font-medium text-gray-600 hover:text-primary-600">
                Sign In
              </Link>
              <Link to="/register" className="btn-primary px-3 py-1.5 sm:px-4 sm:py-2 text-sm">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* ==================== SECTION 1: HERO ==================== */}
      <section className="pt-8 sm:pt-12 pb-10 sm:pb-16 px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="lg:grid lg:grid-cols-2 lg:gap-10 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1.5 bg-primary-50 rounded-lg shadow-neomorph-inset mb-4 sm:mb-6">
                <span className="text-xs font-medium text-primary-700">Trusted by Nigerians everywhere</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 leading-tight">
                Buy Data, Pay Bills
                <span className="text-primary-600 block"> & Advertise Your Business</span>
                For FREE!
              </h1>
              
              <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-500 max-w-xl">
                Fastel makes it easy to recharge your phone, pay for electricity and TV, 
                and connect with other entrepreneurs.
              </p>
              
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/register" className="btn-primary px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base">
                  Join Fastel
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
              
              <div className="mt-6 sm:mt-8 flex items-center space-x-4 sm:space-x-6">
                <div><div className="text-lg sm:text-xl font-bold text-gray-800">10k+</div><div className="text-[10px] sm:text-xs text-gray-500">Active Users</div></div>
                <div className="w-px h-6 sm:h-8 bg-gray-200"></div>
                <div><div className="text-lg sm:text-xl font-bold text-gray-800">₦50M+</div><div className="text-[10px] sm:text-xs text-gray-500">Processed</div></div>
                <div className="w-px h-6 sm:h-8 bg-gray-200"></div>
                <div><div className="text-lg sm:text-xl font-bold text-gray-800">99%</div><div className="text-[10px] sm:text-xs text-gray-500">Success rate</div></div>
              </div>
            </div>
            
            <div className="mt-8 lg:mt-0">
              <div className="relative card p-0 overflow-hidden rounded-2xl shadow-neomorph">
                <img 
                  src="/homepage-login-images/HP1.png" 
                  alt="Fastel App Dashboard"
                  className="w-full h-56 sm:h-64 md:h-72 lg:h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar - Compact */}
      <div className="bg-white border-y border-gray-100 py-3 sm:py-4 shadow-neomorph">
        <div className="content-container px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-primary-50 rounded-lg shadow-neomorph-inset">
                <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-bold text-gray-800">50k+</div>
                <div className="text-[9px] sm:text-xs text-gray-500">Transactions</div>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-primary-50 rounded-lg shadow-neomorph-inset">
                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-bold text-gray-800">10k+</div>
                <div className="text-[9px] sm:text-xs text-gray-500">Users</div>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-primary-50 rounded-lg shadow-neomorph-inset">
                <Award className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-bold text-gray-800">99%</div>
                <div className="text-[9px] sm:text-xs text-gray-500">Uptime</div>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-primary-50 rounded-lg shadow-neomorph-inset">
                <Wallet className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600" />
              </div>
              <div>
                <div className="text-xs sm:text-sm font-bold text-gray-800">₦50M+</div>
                <div className="text-[9px] sm:text-xs text-gray-500">Processed</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== SECTION 2: SERVICES ==================== */}
      <section id="services" className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center px-3 py-1.5 bg-primary-50 rounded-lg shadow-neomorph-inset mb-3 sm:mb-4">
                <Zap className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600 mr-1.5 sm:mr-2" />
                <span className="text-[10px] sm:text-xs font-medium text-primary-700">Services</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
                All Your VTU Services<br />
                <span className="text-primary-600">In One Place</span>
              </h2>
              <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">
                Buy airtime, data bundles, pay electricity bills, and subscribe to your favorite TV services instantly.
              </p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">Recharge any network - MTN, Glo, Airtel, 9mobile</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">Pay electricity bills - IKEDC, Eko Electric, and more</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">TV subscriptions - DStv, GOtv, Startimes</span>
                </div>
              </div>
              <div className="mt-4 sm:mt-6">
                <Link to="/services" className="btn-primary px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base">
                  Explore Services
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="card-padded p-0 overflow-hidden rounded-xl sm:rounded-2xl shadow-neomorph">
                <img 
                  src="/homepage-login-images/HP4.png" 
                  alt="Fastel Services - Airtime, Data, Bills"
                  className="w-full h-56 sm:h-64 md:h-72 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 3: MARKETPLACE ==================== */}
      <section id="marketplace" className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="content-container">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-3 py-1.5 bg-primary-50 rounded-lg shadow-neomorph-inset mb-3 sm:mb-4">
                <Store className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600 mr-1.5 sm:mr-2" />
                <span className="text-[10px] sm:text-xs font-medium text-primary-700">Marketplace</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
                Advertise Your Business<br />
                <span className="text-primary-600">To Thousands</span>
              </h2>
              <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">
                List your business for free and get discovered by thousands of users across Nigeria. No buying or selling - just pure advertising.
              </p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">List your business in under 2 minutes</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">Get discovered by potential customers</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">Renew your listing with VTU purchases</span>
                </div>
              </div>
              <div className="mt-4 sm:mt-6">
                <Link to="/marketplace" className="btn-primary px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base">
                  List Your Business
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div>
              <div className="card-padded p-0 overflow-hidden rounded-xl sm:rounded-2xl shadow-neomorph">
                <img 
                  src="/homepage-login-images/HP2.png" 
                  alt="Fastel Marketplace - Business Directory"
                  className="w-full h-56 sm:h-64 md:h-72 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== SECTION 4: COMMUNITY ==================== */}
      <section id="community" className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="inline-flex items-center px-3 py-1.5 bg-primary-50 rounded-lg shadow-neomorph-inset mb-3 sm:mb-4">
                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary-600 mr-1.5 sm:mr-2" />
                <span className="text-[10px] sm:text-xs font-medium text-primary-700">Community</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
                Connect With<br />
                <span className="text-primary-600">Other Entrepreneurs</span>
              </h2>
              <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">
                Share business tips, discover opportunities, and network with like-minded business owners across Nigeria.
              </p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">Share business insights and tips</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">Discover collaboration opportunities</span>
                </div>
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-xs sm:text-sm text-gray-600">Grow your professional network</span>
                </div>
              </div>
              <div className="mt-4 sm:mt-6">
                <Link to="/community" className="btn-primary px-4 sm:px-5 py-2 sm:py-2.5 text-sm sm:text-base">
                  Join the Community
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <div className="card-padded p-0 overflow-hidden rounded-xl sm:rounded-2xl shadow-neomorph">
                <img 
                  src="/homepage-login-images/HP3.png" 
                  alt="FastConnect Community"
                  className="w-full h-56 sm:h-64 md:h-72 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-4 sm:py-6 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="content-container">
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            <div className="text-center">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md w-fit mx-auto mb-1 sm:mb-2">
                <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
              </div>
              <h3 className="text-[10px] sm:text-xs font-semibold text-gray-800">Bank-level security</h3>
              <p className="text-[8px] sm:text-xs text-gray-500">256-bit encryption</p>
            </div>
            <div className="text-center">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-md w-fit mx-auto mb-1 sm:mb-2">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
              </div>
              <h3 className="text-[10px] sm:text-xs font-semibold text-gray-800">Instant delivery</h3>
              <p className="text-[8px] sm:text-xs text-gray-500">99% success rate</p>
            </div>
            <div className="text-center">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md w-fit mx-auto mb-1 sm:mb-2">
                <Globe className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
              </div>
              <h3 className="text-[10px] sm:text-xs font-semibold text-gray-800">Works nationwide</h3>
              <p className="text-[8px] sm:text-xs text-gray-500">All 36 states</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-4 sm:py-6">
        <div className="content-container px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-3 sm:mb-0">
              <img 
                src="/fastel-logo.png" 
                alt="Fastel" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold bg-gradient-to-r from-primary-700 via-primary-600 to-primary-400 bg-clip-text text-transparent">
                Fastel
              </span>
            </div>
            
            <div className="flex space-x-4 sm:space-x-6 text-[10px] sm:text-xs">
              <button
                onClick={() => openModal('about')}
                className="text-gray-400 hover:text-white transition-colors"
              >
                About
              </button>
              <Link 
                to="/privacy-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy
              </Link>
              <Link 
                to="/terms-of-service"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms
              </Link>
              <button
                onClick={() => openModal('contact')}
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </button>
            </div>
          </div>
          
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-800 text-center text-[10px] sm:text-xs text-gray-500">
            <p>&copy; {new Date().getFullYear()} Fastel Telecom LTD. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* About Modal */}
      {activeModal === 'about' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-neomorph max-w-lg w-full max-h-[80vh] overflow-y-auto border border-primary-100/30">
            <div className="sticky top-0 bg-white border-b border-primary-100/50 px-5 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Info className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">About Fastel</h3>
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 sm:p-2 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
              </button>
            </div>
            <div className="px-5 sm:px-6 py-4 space-y-3 sm:space-y-4">
              <p className="text-xs sm:text-sm text-gray-600">
                Fastel is a Nigerian VTU platform built to make airtime, data, and bill payments easy. We also connect business owners through FastConnect.
              </p>
              <p className="text-xs sm:text-sm text-gray-600">
                We've processed over ₦50M in transactions and serve 10,000+ users across Nigeria.
              </p>
              <div className="bg-primary-50 rounded-lg p-3 sm:p-4 shadow-neomorph-inset">
                <h4 className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 sm:mb-2">What we believe</h4>
                <p className="text-[10px] sm:text-xs text-gray-600">
                  We believe in making financial services accessible and helping Nigerian entrepreneurs grow their businesses.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {activeModal === 'contact' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-neomorph max-w-lg w-full border border-primary-100/30">
            <div className="sticky top-0 bg-white border-b border-primary-100/50 px-5 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Contact Us</h3>
              </div>
              <button
                onClick={closeModal}
                className="p-1.5 sm:p-2 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
              </button>
            </div>
            <div className="px-5 sm:px-6 py-4 space-y-3 sm:space-y-4">
              <div className="flex items-start space-x-3 p-2.5 sm:p-3 bg-neumorph rounded-lg shadow-neomorph-inset">
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 mt-0.5" />
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800">Email</h4>
                  <p className="text-[10px] sm:text-xs text-gray-600">fasteltelecomltd@gmail.com</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">We reply within an hour</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-2.5 sm:p-3 bg-neumorph rounded-lg shadow-neomorph-inset">
                <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 mt-0.5" />
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800">Phone</h4>
                  <p className="text-[10px] sm:text-xs text-gray-600">08109650254, 09042183619</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">Mon-Fri, 9am - 6pm</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-3 p-2.5 sm:p-3 bg-neumorph rounded-lg shadow-neomorph-inset">
                <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600 mt-0.5" />
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-800">Office</h4>
                  <p className="text-[10px] sm:text-xs text-gray-600">NO. 35, MIJINYAWA STREET TUDUN JUKUN</p>
                  <p className="text-[10px] sm:text-xs text-gray-500">ZARIA, KADUNA STATE, NIGERIA</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;